import React, { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useParams } from 'react-router-dom';

import { AccessLevel } from '@bimdata/definitions/const';

import Panel from '@/components/Panel';
import Title from '@/components/Title';
import Tabs from '@/components/Tabs';
import LeftSide from '@/components/LeftSide';
import RightSide from '@/components/RightSize';
import GearIcon from '@/components/icons/Gear';
import Link from '@/components/Link';
import PanelButton from '@/components/PanelButton';
import Banner from '@/components/Banner';
import PageTitle from '@/components/PageTitle';
import CenteringWrapper, { CenteringWrapperProps } from '@/components/CenteringWrapper';

import Layout from '@/pages/Layout';

import { RootState, useAppSelector } from '@/state/store';

import { RequestStatus } from '@/contracts';
import { item, contents, close } from  '@/loaders/project';

import NoModelsPlaceholder from './NoModelsPlaceholder';
import ModelUploadDialog from './ModelUploadDialog';
import ProjectViewer, { ProjectViewerProps } from './ProjectViewer';
import ShareButton from './ShareButton';
import ModelDropCurtain from './ModelDropCurtain';
import dropzone, { DropzoneProps } from '@/components/Dropzone';
import t from '@/utils/translate';
import { ApiError } from '@bimdata/client';
import { ModelData } from '@/state/types/model-data';
import { FileAccumulator } from './FileAccumulator';

const ViewerWithDropzone = dropzone<ProjectViewerProps>(ProjectViewer);
const PlaceholderWithDropzone = dropzone<CenteringWrapperProps>(CenteringWrapper);

const accept = ['.rvt', '.bim'];

const modelValidator = (m: ModelData) => !m.failed && (!!m.elements || !m.ready);

export default () => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const { current: accumulator } = useRef<FileAccumulator>(new FileAccumulator());

  const { id } = useParams();

  if (!id) throw new Error('No project id specified');

  const { current, status, openingError } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.projects.current,
        (state: RootState) => state.projects.openingStatus,
        (state: RootState) => state.projects.openingError,
      ],
      (current, status, openingError) => ({
        current,
        status,
        openingError,
      })
    )
  );

  const [tab, setTab] = useState<string>('display');
  const [dragging, setDragging] = useState(false);

  const loaded = current && current.code === id;

  const reload = useCallback(async () => await (current && contents(current.code)), [current]);
  
  const onDragEnter = useCallback(() => setDragging(true), []);
  const onFileDrop = useCallback((files: File[]) => {
    setDragging(false);
    if (files.length) {
      files.forEach((f) => {
        accumulator.add(f);
      });
      setUploadOpen(true);
    }
  }, []);
  const onDragLeave = useCallback(() => setDragging(false), []);

  useEffect(() => {
    return () => {
      close();
    }
  }, [id]);

  useEffect(() => {
    if (!current || current.code != id) {
      item(id!);
    }
  }, [id, current]);

  const dropzoneProps: DropzoneProps = {
    accept,
    onDragEnter,
    onFileDrop,
    onDragLeave,
    noClick: true,
  }

  const hasData = current?.models?.filter((s) => !!s.models.filter(modelValidator).length).length || false;

  return (<>
    <Layout>
      {!!current && (<Title id="title">{ current?.name }</Title>)}
      <PageTitle>{current && `${current?.name} — BIMDATA`}</PageTitle>
      {loaded && (
        <Panel>
          <LeftSide>
            <Tabs
              value={tab}
              tabs={[
                {
                  key: 'display',
                  title: t('project.title.view'),
                },
                /*
                {
                  key: 'worksheet',
                  title: t('project.title.worksheets'),
                },
                {
                  key: 'views',
                  title: t('project.title.views'),
                }
                */
              ]}
              onTabSelect={setTab}
            />
          </LeftSide>
          <RightSide>
            {!!current && current.access === AccessLevel.OWNER && (
              <ShareButton project={current} />
            )}
            {!!current && current.access !== AccessLevel.READER && (
            <Link to="./settings" exitPoint>
              <PanelButton title={t('project.action.settings')}>
                <GearIcon size={24} />
              </PanelButton>
            </Link>
            )}
          </RightSide>
        </Panel>
      )}
      {!loaded && status === RequestStatus.ERROR && (
        <CenteringWrapper top={60}>
          <Banner>
            {(openingError instanceof ApiError && (openingError.code === 403 || openingError.code === 404))
              ? t('project.error.notFoundOrAccessDenied', true)
              : t('project.error.loadFailed', true)
            }
          </Banner>
        </CenteringWrapper>
      ) }
      { loaded && (
        (current.access === AccessLevel.READER)
          ? (
            (hasData)
            ? (<ProjectViewer generateThumbnails top={113} modelValidator={modelValidator} project={current}/>)
            : (<CenteringWrapper top={60}><Banner>{t('project.message.empty', true)}</Banner></CenteringWrapper>)
          )
          : (
            <>
            {(hasData)
              ? (
                <ViewerWithDropzone
                  generateThumbnails
                  modelValidator={modelValidator}
                  top={113}
                  project={current}
                  {...dropzoneProps}
                />
              )
              : (
                <PlaceholderWithDropzone
                  top={113}
                  {...dropzoneProps}
                >
                  <NoModelsPlaceholder onBtnClick={() => setUploadOpen(true)} />
                </PlaceholderWithDropzone>
              )}
              <ModelUploadDialog
                project={current}
                open={uploadOpen}
                files={(uploadOpen && !accumulator.empty) ? accumulator.files : undefined}
                onClose={() => {
                  setUploadOpen(false);
                  accumulator.dispose();
                  reload();
                }}
              />
            </>
          )
      )}
    </Layout>
    {!!dragging && <ModelDropCurtain/>}
  </>);
};
